/**
 * C.4 — Browser-only vault parser.
 *
 * Ports the regexes from
 * `with_obsidian/localvaultexplorer/server/src/services/vaultImporter.ts`
 * into a pure-TS module with zero Node deps. Operates on strings; callers
 * supply the file bytes from whatever source (Graph driveItem content,
 * showDirectoryPicker handles, drag-drop entries, JSZip entries).
 *
 * Frontmatter: YAML between `---\n` fences at the top.
 * Wikilinks:   [[Path/To/Note]] or [[Path/To/Note|alias]] or [[Path#section]]
 * Embeds:      ![[Note]] or ![[image.png]]
 * Mdlinks:     [text](URL) where URL is not http/https/mailto
 * Tags:        #word (alphanumeric, `_`, `-`, `/`)
 * Callouts:    > [!kind] text (renders are deferred to TipTap; parser
 *              only flags presence so Bases queries can filter on them).
 *
 * Output shape mirrors the Convex `vaultNotes` table.
 */

import { parse as parseYaml } from 'yaml';

export interface ParsedNote {
  /** Vault-relative path including extension, e.g. `Cases/Hadley v. Baxendale.md`. */
  path: string;
  /** Filename without extension, e.g. `Hadley v. Baxendale`. */
  name: string;
  /** Full markdown body (after the YAML frontmatter is stripped). */
  body: string;
  /** Original full file content including frontmatter. */
  raw: string;
  /** Parsed YAML frontmatter object (empty if none). */
  frontmatter: Record<string, unknown>;
  /** Wikilink targets, deduplicated, in order of first appearance. */
  wikilinks: string[];
  /** Embeds (`![[…]]`), deduplicated, in order of first appearance. */
  embeds: string[];
  /** Markdown link URLs that point to vault-internal paths. */
  markdownLinks: string[];
  /** Tag names without the leading `#`, deduplicated. */
  tags: string[];
  /** Callout kinds (`note`, `warning`, `info`, `quote`, `example`, `todo`, …). */
  callouts: string[];
}

const FRONTMATTER_REGEX = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;
const WIKILINK_REGEX = /\[\[([^\]|#\n]+?)(?:#[^\]|\n]+)?(?:\|[^\]\n]+)?\]\]/g;
const EMBED_REGEX = /!\[\[([^\]\n]+?)\]\]/g;
const MDLINK_REGEX = /(?<!\!)\[([^\]\n]+)\]\(([^)\n]+)\)/g;
const TAG_REGEX = /(?:^|\s)#([A-Za-z0-9_/-]+)/g;
const CALLOUT_REGEX = /^>\s*\[!([A-Za-z][A-Za-z0-9-]*)\]/gm;

function pushUnique(into: string[], value: string): void {
  if (!into.includes(value)) into.push(value);
}

/**
 * Strip the frontmatter fence and parse it.
 * Returns `{ frontmatter: {}, body: input }` if no frontmatter is present
 * or if the YAML fails to parse (we never throw — vault content can be
 * messy in the wild and we'd rather surface a broken note than refuse
 * to load the vault).
 */
export function splitFrontmatter(input: string): {
  frontmatter: Record<string, unknown>;
  body: string;
} {
  const match = input.match(FRONTMATTER_REGEX);
  if (!match) return { frontmatter: {}, body: input };
  try {
    const parsed = parseYaml(match[1]!) as unknown;
    const frontmatter =
      parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : {};
    return { frontmatter, body: input.slice(match[0].length) };
  } catch {
    return { frontmatter: {}, body: input };
  }
}

function extractWikilinks(body: string): { wikilinks: string[]; embeds: string[] } {
  const wikilinks: string[] = [];
  const embeds: string[] = [];

  // Embeds first so we don't double-count the wikilink inside `![[…]]`.
  // We rebuild `body` with embeds blanked out for the wikilink pass.
  let withoutEmbeds = body;
  let m: RegExpExecArray | null;
  EMBED_REGEX.lastIndex = 0;
  while ((m = EMBED_REGEX.exec(body)) !== null) {
    pushUnique(embeds, m[1]!.trim());
  }
  withoutEmbeds = body.replace(EMBED_REGEX, (full) => ' '.repeat(full.length));

  WIKILINK_REGEX.lastIndex = 0;
  while ((m = WIKILINK_REGEX.exec(withoutEmbeds)) !== null) {
    pushUnique(wikilinks, m[1]!.trim());
  }
  return { wikilinks, embeds };
}

function extractMarkdownLinks(body: string): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  MDLINK_REGEX.lastIndex = 0;
  while ((m = MDLINK_REGEX.exec(body)) !== null) {
    const url = m[2]!.trim();
    // Skip absolute URLs and protocol-prefixed; those aren't vault-internal.
    if (/^[a-z][a-z0-9+.-]*:/i.test(url)) continue;
    if (url.startsWith('//')) continue;
    pushUnique(out, url);
  }
  return out;
}

function extractTags(body: string): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  TAG_REGEX.lastIndex = 0;
  while ((m = TAG_REGEX.exec(body)) !== null) {
    pushUnique(out, m[1]!);
  }
  return out;
}

function extractCallouts(body: string): string[] {
  const out: string[] = [];
  let m: RegExpExecArray | null;
  CALLOUT_REGEX.lastIndex = 0;
  while ((m = CALLOUT_REGEX.exec(body)) !== null) {
    pushUnique(out, m[1]!.toLowerCase());
  }
  return out;
}

/**
 * Parse a single markdown file into a ParsedNote.
 *
 * `path` is the vault-relative path (`Cases/Hadley v. Baxendale.md`).
 * `raw` is the file's full UTF-8 string content.
 *
 * Frontmatter `tags` arrays are merged with body-extracted `#tag` hits.
 */
export function parseVaultNote(path: string, raw: string): ParsedNote {
  const { frontmatter, body } = splitFrontmatter(raw);
  const { wikilinks, embeds } = extractWikilinks(body);
  const markdownLinks = extractMarkdownLinks(body);
  const bodyTags = extractTags(body);
  const callouts = extractCallouts(body);

  const frontmatterTags: string[] = [];
  const fmTags = frontmatter.tags;
  if (Array.isArray(fmTags)) {
    for (const t of fmTags) {
      if (typeof t === 'string') pushUnique(frontmatterTags, t);
    }
  } else if (typeof fmTags === 'string') {
    pushUnique(frontmatterTags, fmTags);
  }
  const tags = [...frontmatterTags];
  for (const t of bodyTags) pushUnique(tags, t);

  // Derive name from the basename without the .md extension.
  const slash = path.lastIndexOf('/');
  const basename = slash >= 0 ? path.slice(slash + 1) : path;
  const dot = basename.lastIndexOf('.');
  const name = dot > 0 ? basename.slice(0, dot) : basename;

  return {
    path,
    name,
    raw,
    body,
    frontmatter,
    wikilinks,
    embeds,
    markdownLinks,
    tags,
    callouts,
  };
}

/**
 * The two known whiteboard formats: Repancy's native `.flow` (this
 * milestone) and Obsidian's `.canvas` (deferred to FT-1). The parser
 * only validates that `.flow` files are JSON with a `nodes` + `edges`
 * shape; node/edge content stays opaque to keep migration cheap.
 */
export interface ParsedFlow {
  path: string;
  name: string;
  raw: string;
  version: number;
  flowName: string;
  nodes: unknown[];
  edges: unknown[];
}

export function parseFlowFile(path: string, raw: string): ParsedFlow {
  let json: unknown;
  try {
    json = JSON.parse(raw);
  } catch (err) {
    throw new Error(
      `parseFlowFile: ${path} is not valid JSON (${(err as Error).message})`,
    );
  }
  if (!json || typeof json !== 'object' || Array.isArray(json)) {
    throw new Error(`parseFlowFile: ${path} root must be an object`);
  }
  const obj = json as Record<string, unknown>;
  const nodes = Array.isArray(obj.nodes) ? obj.nodes : [];
  const edges = Array.isArray(obj.edges) ? obj.edges : [];

  const slash = path.lastIndexOf('/');
  const basename = slash >= 0 ? path.slice(slash + 1) : path;
  const dot = basename.lastIndexOf('.');
  const name = dot > 0 ? basename.slice(0, dot) : basename;

  return {
    path,
    name,
    raw,
    version: typeof obj.version === 'number' ? obj.version : 1,
    flowName: typeof obj.name === 'string' ? obj.name : name,
    nodes,
    edges,
  };
}

/**
 * Vault-wide adjacency: given a list of parsed notes, return rows
 * suitable for the `vaultLinks` Convex table.
 */
export interface VaultLinkRow {
  fromNotePath: string;
  toNotePath: string;
  kind: 'wikilink' | 'mdlink' | 'embed' | 'tag';
}

export function buildVaultLinks(notes: ParsedNote[]): VaultLinkRow[] {
  // Build a resolver that turns a wikilink target ("Cases/Hadley v. Baxendale"
  // or "Hadley v. Baxendale") into a concrete vault note path.
  const byPath = new Map<string, string>();
  const byName = new Map<string, string>();
  for (const n of notes) {
    const noExt = n.path.replace(/\.md$/i, '');
    byPath.set(noExt, n.path);
    byName.set(n.name, n.path);
  }

  function resolve(target: string): string | undefined {
    const cleaned = target.trim();
    return byPath.get(cleaned) ?? byName.get(cleaned);
  }

  const rows: VaultLinkRow[] = [];
  for (const note of notes) {
    for (const target of note.wikilinks) {
      const to = resolve(target);
      if (to) rows.push({ fromNotePath: note.path, toNotePath: to, kind: 'wikilink' });
    }
    for (const target of note.embeds) {
      const to = resolve(target);
      if (to) rows.push({ fromNotePath: note.path, toNotePath: to, kind: 'embed' });
    }
    for (const url of note.markdownLinks) {
      const to = resolve(url) ?? resolve(url.replace(/\.md$/i, ''));
      if (to) rows.push({ fromNotePath: note.path, toNotePath: to, kind: 'mdlink' });
    }
  }
  return rows;
}
